import { useMemo, useRef } from "preact/hooks"
import Icon from "@/elements/Icon/Icon"
import { SearchTermsFacet } from "@nosto/nosto-js/client"
import { useOptimisticFacet } from "@/hooks/useOptimisticFacet"
import { useNostoAppState } from "@nosto/search-js/preact/hooks"
import { cl } from "@nosto/search-js/utils"
import styles from "./TermsFacet.module.css"
import {
  dedupeFacetTerms,
  humanizeFacetValue,
  isColorFacet,
  mergeColorFacetTerms,
  parseColorValue
} from "@/utils/facetHelpers"
import { countFacetValuesByFamily } from "@/utils/facetCountFromHits"
import { useStyleFamilyCatalog } from "@/contexts/StyleFamilyCatalogContext"
import { useGridResult } from "@/contexts/GridResultContext"
import { useHiddenFacetHandles } from "@/contexts/HiddenFacetsContext"
import { STATIC_HIDDEN_FACET_HANDLES, facetHandle } from "@/utils/facetVisibility"
import { collapsiblePanelProps } from "@/utils/collapsiblePanel"

type Props = {
  facet: SearchTermsFacet
}

function toHandle(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")
}

export default function TermsFacet({ facet }: Props) {
  const { active, toggleActive, optimisticData, selectedFiltersCount, toggleProductFilter } = useOptimisticFacet(facet)
  const catalog = useStyleFamilyCatalog()
  const initialized = useNostoAppState(state => state.initialized) ?? false
  const loading = useNostoAppState(state => state.loading) ?? false
  const gridResult = useGridResult()
  const hiddenFacetHandles = useHiddenFacetHandles()

  const isColor = isColorFacet(facet)
  const dedupedData = useMemo(() => {
    const cased = dedupeFacetTerms(optimisticData)
    return isColor ? mergeColorFacetTerms(cased) : cased
  }, [optimisticData, isColor])

  // Stash the most recent valid recount map. While catalog re-fetches (ready -> loading -> ready),
  // Nosto's raw facet data briefly reverts to global counts and includes terms the recount would
  // normally drop: visible as the option list growing then shrinking on toggle.
  // Re-applying the cached counts on top of current `dedupedData` keeps the displayed list stable
  // while still honoring optimistic `selected` flags from the user's click.
  const lastCountsRef = useRef<Map<string, number> | null>(null)

  const recountedData = useMemo(() => {
    // Authoritative-zero check: Nosto has responded (initialized) and reports zero
    // matching products for the current filter combination — either via `rawTotal`
    // (catalog status becomes "idle" before any deduped fetch) or via the catalog
    // returning a ready-but-empty result. Surface zero counts in both cases so the
    // user never sees "Cotton (1)" sitting on an empty grid.
    // Authoritative zero is whatever the user actually sees in the product area.
    // `GridResultContext` is populated by `ContentWrapper` and reflects exactly
    // the decision between `<Products />` and `<NoResults />`. When the user
    // sees no products — whether because the raw response was empty, the
    // catalog deduped to zero, OR every rendered dynamic card resolved empty —
    // facet counts must collapse to zero to match.
    //
    // Falls back to catalog state when the context isn't present (e.g. unit
    // tests rendering TermsFacet in isolation).
    const zeroAuthoritative =
      initialized &&
      !loading &&
      (gridResult
        ? !gridResult.hasVisibleResults
        : catalog != null &&
          (catalog.rawTotal === 0 ||
            (catalog.status === "ready" && (!catalog.dedupedHits || catalog.dedupedHits.length === 0))))

    let counts: Map<string, number> | null = null
    if (zeroAuthoritative) {
      counts = new Map()
    } else if (catalog?.status === "ready" && catalog.renderableHits && catalog.renderableHits.length > 0) {
      // Count by *family availability* over the pre-dedupe renderable hits so
      // the displayed count stays stable when the user selects a value. If we
      // counted post-dedupe representatives instead, e.g. "Color: Black (8)"
      // would jump to "Color: Black (13)" the moment the user selected it,
      // because every family's representative then becomes Black
      //
      // For color facets, normalize raw product values to the parsed display
      // name so `"Black"` and `"Black|#000000"` both bucket under `"black"`
      // (matches the secondary merge in `mergeColorFacetTerms`).
      const normalize = isColor
        ? (value: string) => parseColorValue(value).name.toLowerCase()
        : undefined
      const fresh = countFacetValuesByFamily(catalog.renderableHits, facet.field, normalize)
      if (fresh.size > 0) {
        counts = fresh
        lastCountsRef.current = fresh
      } else {
        // Hits exist but the field path didn't resolve against any product
        // (decorator stripped it, facet `field` shape changed, etc.). Prefer
        // the last good recount over Nosto's raw counts.
        counts = lastCountsRef.current
      }
    } else {
      // Loading / idle / fallback (with rawTotal > 0) — keep the last good
      // counts to avoid flicker while the catalog re-fetches.
      counts = lastCountsRef.current
    }
    if (!counts) {
      // No catalog ever produced a good recount (initial pre-ready render).
      // Surface Nosto's raw counts so the user sees the facet at all — losing
      // a chip is worse than a small count skew from style-family dedupe.
      return dedupedData
    }
    return dedupedData
      .map(term => {
        const raw = String(term.value ?? "")
        const lookupKey = isColor ? parseColorValue(raw).name.toLowerCase() : raw.toLowerCase()
        return { ...term, count: counts.get(lookupKey) ?? 0 }
      })
      .filter(term => term.count > 0)
  }, [
    dedupedData,
    catalog?.status,
    catalog?.dedupedHits,
    catalog?.renderableHits,
    catalog?.rawTotal,
    facet.field,
    facet.name,
    initialized,
    loading,
    gridResult?.hasVisibleResults,
    isColor
  ])

  const handle = facetHandle(facet.name)
  if (STATIC_HIDDEN_FACET_HANDLES.has(handle) || hiddenFacetHandles.has(handle)) {
    return null
  }

  if (recountedData.length === 0) {
    return null
  }

  // Hide single-value facets that the user hasn't engaged with — on scoped
  // PLPs (e.g. `/collections/mens-t-shirts`) the only available value for
  // Gender / Product Type is implied by the collection itself, so the facet
  // is redundant. If the user has actively selected the
  // term in this facet, keep it visible so they can remove the chip.
  if (recountedData.length === 1 && selectedFiltersCount === 0) {
    return null
  }

  return (
    <li className={cl(styles.dropdown, active && styles.active)}>
      <button
        className={styles.anchor}
        data-nosto-element="facet"
        onClick={toggleActive}
        onKeyDown={e => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault()
            toggleActive()
          }
        }}
        aria-controls={`${facet.id}-sub-menu`}
        aria-expanded={!!active}
        aria-label={`${active ? "Collapse" : "Expand"} ${facet.name}`}
        type="button"
      >
        <span className={styles.title}>{facet.name}</span>
        {selectedFiltersCount > 0 && <span className={styles.count}>{selectedFiltersCount}</span>}
        <span className={styles.icon}>
          <Icon name={active ? "minus" : "plus"} />
        </span>
      </button>
      <div className={styles.menu} id={`${facet.id}-sub-menu`} {...collapsiblePanelProps(!!active)}>
        {isColor ? (
          <ColorOptions
            facet={facet}
            items={recountedData}
            toggleProductFilter={toggleProductFilter}
            active={!!active}
          />
        ) : (
          <CheckboxOptions
            facet={facet}
            items={recountedData}
            toggleProductFilter={toggleProductFilter}
            active={!!active}
          />
        )}
      </div>
    </li>
  )
}

type OptionListProps = {
  facet: SearchTermsFacet
  items: ReturnType<typeof dedupeFacetTerms>
  toggleProductFilter: (field: string, value: string, selected: boolean) => void
  active: boolean
}

function toggleAllVariants(
  field: string,
  variants: string[],
  selected: boolean,
  toggleProductFilter: (field: string, value: string, selected: boolean) => void
) {
  variants.forEach(variant => toggleProductFilter(field, variant, selected))
}

function CheckboxOptions({ facet, items, toggleProductFilter, active }: OptionListProps) {
  return (
    <ul className={styles.options} role="menu">
      {items.map(item => {
        const variants = item.variants.length > 0 ? item.variants : [item.value]
        const inputId = `filters-facets-${toHandle(item.value)}-${toHandle(facet.name ?? "")}`
        return (
          <li key={item.value} className={styles.option} role="menuitemcheckbox" aria-checked={item.selected}>
            <label className={styles.checkboxLabel} htmlFor={inputId}>
              <input
                id={inputId}
                type="checkbox"
                className={styles.checkboxInput}
                checked={item.selected}
                onChange={() => toggleAllVariants(facet.field, variants, !item.selected, toggleProductFilter)}
                tabIndex={active ? 0 : -1}
              />
              <span className={styles.checkboxIndicator} aria-hidden="true" />
              <span className={styles.checkboxText}>
                {humanizeFacetValue(item.value)}
                <span className={styles.optionCount}> ({item.count})</span>
              </span>
            </label>
          </li>
        )
      })}
    </ul>
  )
}

function ColorOptions({ facet, items, toggleProductFilter, active }: OptionListProps) {
  return (
    <ul className={cl(styles.options, styles.colorGrid)} role="menu">
      {items.map(item => {
        const variants = item.variants.length > 0 ? item.variants : [item.value]
        const { name, swatch, isImage } = parseColorValue(item.value)
        const inputId = `filters-facets-color-${toHandle(name)}`
        const swatchStyle = !isImage && swatch ? { backgroundColor: swatch } : undefined
        const needsBorder = !isImage && swatch.toLowerCase() === "#ffffff"
        return (
          <li
            key={item.value}
            className={styles.option}
            role="menuitemcheckbox"
            aria-checked={item.selected}
          >
            <label className={styles.checkboxLabel} htmlFor={inputId}>
              <input
                id={inputId}
                type="checkbox"
                className={styles.checkboxInput}
                checked={item.selected}
                onChange={() => toggleAllVariants(facet.field, variants, !item.selected, toggleProductFilter)}
                tabIndex={active ? 0 : -1}
              />
              <span className={styles.checkboxIndicator} aria-hidden="true" />
              <span className={styles.checkboxText}>
                <span
                  className={cl(styles.colorDot, needsBorder && styles.colorDotBordered)}
                  style={swatchStyle}
                  aria-hidden="true"
                >
                  {isImage && <img src={swatch} alt="" width={16} height={16} />}
                </span>
                {humanizeFacetValue(name)}
                <span className={styles.optionCount}> ({item.count})</span>
              </span>
            </label>
          </li>
        )
      })}
    </ul>
  )
}
